import {
  faArrowDownShortWide,
  faArrowDownWideShort,
  faFilter,
  faLocationDot,
  faFileDownload,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useGetRequest from '../../api/useGetRequest';
import EmptyList from '../../components/agv-empty-list';
import GenericPageContainer from '../../components/agv-generic-page-container';
import GenericTable from '../../components/agv-generic-table';
import GenericTableSkeleton from '../../components/agv-generic-table/genericTableSkeleton';
import SearchFilter from '../../components/agv-header-search';
import SelectFilter from '../../components/agv-header-select';
import { STATUS_ENUM } from '../../components/agv-status/status-enum';
import { ORDER_BY_ENUM } from '../../enums/orderby.enum';
import AddressModal from './address-modal';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import HeaderButton from '../../components/agv-header-button';
import fleetApi from '../../services/api.service';

export default function Addresses() {
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [addressSelected, setAddressSelected] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const features = useSelector((state) => state.features);

  const initialFilters = {
    limit: 24,
    offset: 0,
    query: searchParams.get('query') || '',
    order:
      searchParams.get('order') === ORDER_BY_ENUM.ASC ||
      searchParams.get('order') === ORDER_BY_ENUM.DESC
        ? searchParams.get('order')
        : ORDER_BY_ENUM.ASC,
    order_field: searchParams.get('order_field') || 'address_key',
    without_photos_only:
      searchParams.get('without_photos_only') &&
      (searchParams.get('without_photos_only') === 'true' ||
        searchParams.get('without_photos_only') === 'false')
        ? searchParams.get('without_photos_only')
        : 'all',
  };

  const {
    items,
    hasMore,
    filters,
    fetchMoreData,
    handleUpdateParam: originalHandleUpdateParam,
    isLoading,
  } = useInfiniteScroll({
    initialFilters,
    debounceTime: 300,
    getRequest: useGetRequest,
    endpoint: '/addresses',
  });

  /**
   * Wrapper pour handleUpdateParam qui gère le cas spécial 'all' pour without_photos_only
   */
  const handleUpdateParam = (key, value) => {
    if (key === 'without_photos_only') {
      if (value === 'all') {
        // Supprimer le paramètre des filtres si c'est 'all'
        originalHandleUpdateParam(key, null);
        return;
      }
    }
    originalHandleUpdateParam(key, value);
  };

  const onOpenModal = (element) => {
    setAddressSelected(element);
    setIsOpen(true);
  };

  /**
   * Handle export to XLSX
   */
  const handleExportAddresses = async () => {
    try {
      setIsExporting(true);

      const params = new URLSearchParams();
      if (filters.query) params.append('query', filters.query);
      if (
        filters.without_photos_only &&
        filters.without_photos_only !== 'all'
      ) {
        params.append('without_photos_only', filters.without_photos_only);
      }
      params.append('order', filters.order);
      params.append('order_field', filters.order_field);

      const queryString = params.toString();

      const endpoint = `/addresses/export/xlsx?${queryString}`;

      const response = await fleetApi.get(endpoint, {
        responseType: 'blob',
      });

      const blob = response.data;

      if (blob.size === 0) {
        throw new Error('Le fichier XLSX est vide - aucune donnée disponible');
      }

      const filename = `destinataires-${new Date().toISOString().split('T')[0]}.xlsx`;
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('[XLSX Export] Erreur:', error);
      alert(`Erreur lors de l'export des destinataires : ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Format the full address
   * @param {object} element - The address element
   * @returns {string} - The formatted address
   */
  const formatFullAddress = (element) => {
    const parts = [];
    if (element?.address_number) parts.push(element.address_number);
    if (element?.street) parts.push(element.street);
    if (element?.zipcode) parts.push(element.zipcode);
    if (element?.city) parts.push(element.city);
    if (element?.country) parts.push(element.country);
    return parts.join(', ') || '-';
  };

  /**
   * Format the title with address_key and name
   * @param {object} element - The address element
   * @returns {string} - The formatted title
   */
  const formatTitle = (element) => {
    const addressKey = element?.address_key || 'Non renseigné';
    const name = element?.name || '';

    // Si les deux sont identiques ou si le name est vide, n'affiche que l'address_key
    if (!name || addressKey === name) {
      return addressKey;
    }

    return `${addressKey} - ${name}`;
  };

  /**
   * Generate fields for the table row
   * @param {object} element - The element to generate fields for
   * @returns {array} - The fields array
   */
  const generateFields = (element) => {
    const fields = [
      {
        name: 'Dernière livraison',
        value: element?.last_delivery_date
          ? moment(new Date(element.last_delivery_date)).format('DD MMM YYYY')
          : '-',
      },
      {
        name: 'Nombre de livraisons',
        value: element?.total_deliveries || '0',
      },
      {
        name: 'Nombre de photos',
        value: element?.photos_count || '0',
      },
    ];

    return fields;
  };

  return (
    <>
      <GenericPageContainer
        title={'Liste des destinataires'}
        subtitle={
          'Retrouvez la liste de vos destinataires et leurs informations'
        }
        filters={
          <>
            <SearchFilter
              value={filters.query}
              setValue={(value) => handleUpdateParam('query', value)}
            />
            <SelectFilter
              value={filters.order}
              onChange={(value) => handleUpdateParam('order', value)}
              options={[
                { value: ORDER_BY_ENUM.ASC, name: 'Asc.' },
                { value: ORDER_BY_ENUM.DESC, name: 'Desc.' },
              ]}
              icon={
                filters.order === ORDER_BY_ENUM.ASC
                  ? faArrowDownShortWide
                  : faArrowDownWideShort
              }
            />
            <SelectFilter
              value={filters.without_photos_only}
              onChange={(value) =>
                handleUpdateParam('without_photos_only', value)
              }
              options={[
                { value: 'all', name: 'Tous les destinataires' },
                { value: true, name: 'Sans photos uniquement' },
                { value: false, name: 'Avec photos uniquement' },
              ]}
              icon={faFilter}
            />
            {features?.OTHERS?.BACKOFFICE?.ADDRESSES?.canExportXlsx && (
              <HeaderButton
                onClick={handleExportAddresses}
                disabled={isExporting}
                text={
                  isExporting ? 'Exportation en cours...' : 'Exporter en XLSX'
                }
                icon={isExporting ? faSpinner : faFileDownload}
              />
            )}
          </>
        }
      >
        {isLoading && filters.offset === 0 ? (
          <GenericTableSkeleton numberOfRows={8} />
        ) : items?.length > 0 ? (
          <InfiniteScroll
            dataLength={items.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={<GenericTableSkeleton numberOfRows={2} />}
            scrollableTarget="page-content"
            endMessage={
              <p className="agrivitech-pagination-text tw-mb-2 tw-mt-8 tw-text-center">
                Vous avez vu toutes les données pour cette recherche
              </p>
            }
          >
            <GenericTable
              isLoading={isLoading}
              rows={items?.map((element) => ({
                title: formatTitle(element),
                subtitle: formatFullAddress(element),
                onClick: () => onOpenModal(element),
                buttonText: 'Détails',
                status: STATUS_ENUM.NEUTRAL,
                icon: faLocationDot,
                fields: generateFields(element),
              }))}
              iconColor={'tw-text-checklist-common'}
            />
          </InfiniteScroll>
        ) : (
          <EmptyList />
        )}
      </GenericPageContainer>
      <AddressModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        address={addressSelected}
      />
    </>
  );
}
